import { Suspense, lazy, useEffect, useState } from "react";

import { Box, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

import { Translate } from "@/components/Application/Translate";
import { MeasurementContext } from "@/components/Measurements/Context/selectedMeasurementContext";
import { ManualMeasurementPointSummary } from "@/components/Measurements/Types/MeasurementGroups";

import DashboardGrid from "@/helpers/components/Dashboard/DashboardGrid";
import ComponentError from "@/helpers/components/Errors/ComponentError";

const MeasurementChartsContainer = lazy(
	() =>
		import(
			"@/components/Measurements/Components/Charts/MeasurementChartsContainer"
		),
);

const ButtonContainer = lazy(
	() => import("@/components/Measurements/Components/Buttons/Container"),
);

const MeasurementGroupList = lazy(
	() =>
		import(
			"@/components/Measurements/Components/FileTree/MeasurementGroupList"
		),
);
const RecordsTable = lazy(
	() =>
		import(
			"@/components/Measurements/Components/RecordsTable/MeasurementsRecordsTable"
		),
);
const EditMeasurementSkeleton = lazy(
	() =>
		import(
			"@/components/Measurements/Components/Skeletons/EditMeasurementSkeleton"
		),
);
const LineChartSkeleton = lazy(
	() =>
		import("@/components/Measurements/Components/Skeletons/LineChartSkeleton"),
);

const EditMeasurement = lazy(
	() =>
		import(
			"@/components/Measurements/Components/EditMeasurement/EditMeasurement"
		),
);

type Props = {
	initialSelectedMeasurement?: ManualMeasurementPointSummary | null;
};

export const MeasurementsHomePage = ({
	initialSelectedMeasurement = null,
}: Props) => {
	const [selectedMeasurement, selectMeasurement] =
		useState<ManualMeasurementPointSummary | null>(null);
	const [groupId, setGroupId] = useState<number | null>(null);

	useEffect(() => {
		if (initialSelectedMeasurement) {
			selectMeasurement(initialSelectedMeasurement);
		}
	}, []);

	return (
		<MeasurementContext.Provider
			value={{ selectedMeasurement, groupId, selectMeasurement, setGroupId }}
		>
			<DashboardGrid
				dashboardHeader={
					<Translate String="Measurements.home.Header">Measurements</Translate>
				}
				buttons={<ButtonContainer />}
			>
				<Box sx={{ gridColumn: { xs: "span 0", lg: "span 4" } }}>
					<QueryErrorResetBoundary>
						{({ reset }) => (
							<Sentry.ErrorBoundary
								onReset={reset}
								fallback={({ resetError }) => (
									<ComponentError reset={resetError} />
								)}
							>
								<MeasurementGroupList />
							</Sentry.ErrorBoundary>
						)}
					</QueryErrorResetBoundary>
				</Box>
				<Box
					sx={{
						gridColumn: { xs: "span 8", sm: "span 4", lg: "span 3" },
						gridRow: "span 2",
					}}
				>
					{!selectedMeasurement && (
						<Typography component="h4">Select a measurement</Typography>
					)}
					<QueryErrorResetBoundary>
						{({ reset }) => (
							<Sentry.ErrorBoundary
								onReset={reset}
								fallback={({ resetError }) => (
									<ComponentError reset={resetError} />
								)}
							>
								<Suspense fallback={<EditMeasurementSkeleton />}>
									<EditMeasurement />
								</Suspense>
							</Sentry.ErrorBoundary>
						)}
					</QueryErrorResetBoundary>
				</Box>
				<Box
					sx={{
						gridColumn: { xs: "span 12", sm: "span 7", lg: "span 5" },
						gridRow: { xs: "3 / span 2", sm: "span 2" },
					}}
				>
					<QueryErrorResetBoundary>
						{({ reset }) => (
							<Sentry.ErrorBoundary
								onReset={reset}
								fallback={({ resetError }) => (
									<ComponentError reset={resetError} />
								)}
							>
								<MeasurementChartsContainer />
							</Sentry.ErrorBoundary>
						)}
					</QueryErrorResetBoundary>
				</Box>
				<Box
					sx={{
						gridColumn: { xs: "span 12", lg: "5 /span 8" },
						gridRow: { xs: "5 / span 2", sm: "3 /span 2" },
						alignSelf: { xs: "start" },
						overflow: "auto",
					}}
				>
					<QueryErrorResetBoundary>
						{({ reset }) => (
							<Sentry.ErrorBoundary
								onReset={reset}
								fallback={({ resetError }) => (
									<ComponentError reset={resetError} />
								)}
							>
								<RecordsTable />
							</Sentry.ErrorBoundary>
						)}
					</QueryErrorResetBoundary>
				</Box>
			</DashboardGrid>
		</MeasurementContext.Provider>
	);
};
